import React, { useState } from 'react';
import { Box, IconButton, Menu, MenuItem, Slider, Tooltip, Typography } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrowOutlined';
import PauseIcon from '@mui/icons-material/PauseOutlined';
import Replay5Icon from '@mui/icons-material/Replay5Outlined';
import Forward5Icon from '@mui/icons-material/Forward5Outlined';
import Replay10Icon from '@mui/icons-material/Replay10Outlined';
import Forward10Icon from '@mui/icons-material/Forward10Outlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeftOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRightOutlined';
import VolumeUpIcon from '@mui/icons-material/VolumeUpOutlined';
import VolumeDownIcon from '@mui/icons-material/VolumeDownOutlined';
import VolumeOffIcon from '@mui/icons-material/VolumeOffOutlined';
import FullscreenIcon from '@mui/icons-material/FullscreenOutlined';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExitOutlined';
import RepeatIcon from '@mui/icons-material/RepeatOutlined';
import FastRewindIcon from '@mui/icons-material/FastRewindOutlined';
import FastForwardIcon from '@mui/icons-material/FastForwardOutlined';
import type { MediaPlayer } from '../Timeline/useMediaPlayer';
import { formatTimecode } from '../../utils/timecode';
import { markOnDark, studioDark } from './studioTokens';

export const PLAYBACK_SPEEDS = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2];

export interface ScrubMarker {
  time: number;
  endTime?: number | null;
  color: string;
}

interface TransportBarProps {
  player: MediaPlayer;
  fps: number;
  disabled?: boolean;
  /** Notes drawn on the slim scrub bar. */
  markers?: ScrubMarker[];
  loop?: {
    enabled: boolean;
    available: boolean;
    onToggle: () => void;
  };
  inPoint?: number | null;
  outPoint?: number | null;
  isFullscreen?: boolean;
  onToggleFullscreen?: () => void;
  /** Extra controls at the far right (before fullscreen). */
  extra?: React.ReactNode;
  /** Narrow layout (e.g. a side panel): fewer buttons, no volume slider. */
  compact?: boolean;
}

const TIMECODE_FONT = '"JetBrains Mono", "SFMono-Regular", Menlo, Consolas, monospace';

/** Read by screen readers, not shown. (Pixel strings: in `sx`, a width of 1 means 100%.) */
const visuallyHidden = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  p: 0,
  m: '-1px',
  border: 0,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const;

/** Keep keyboard focus where it was so Space keeps controlling playback. */
const keepFocus = (event: React.MouseEvent) => event.preventDefault();

const controlSx = {
  color: studioDark.textMuted,
  '&:hover': { color: studioDark.text, bgcolor: studioDark.hover },
  '&.Mui-disabled': { color: studioDark.disabled },
  '&:focus-visible': { outline: `2px solid ${studioDark.focusRing}`, outlineOffset: 1 },
} as const;

/**
 * Viewer transport in the style of an NLE's source monitor: a slim scrub bar
 * with note ticks, frame/skip/shuttle controls, a frame-accurate timecode
 * readout, speed, loop, volume and fullscreen. Dark, to sit under the video.
 */
const TransportBar: React.FC<TransportBarProps> = ({
  player,
  fps,
  disabled = false,
  markers = [],
  loop,
  inPoint = null,
  outPoint = null,
  isFullscreen = false,
  onToggleFullscreen,
  extra,
  compact = false,
}) => {
  const wideOnly = compact ? 'none' : { xs: 'none', md: 'inline-flex' };
  const [speedAnchor, setSpeedAnchor] = useState<HTMLElement | null>(null);
  const { duration, currentTime, playing, shuttle } = player;
  const percent = (seconds: number) => (duration > 0 ? Math.min(100, Math.max(0, (seconds / duration) * 100)) : 0);
  const shuttling = shuttle < 0 || shuttle > 1;

  return (
    <Box sx={{ bgcolor: studioDark.surface, color: studioDark.text, px: { xs: 1, sm: 1.5 }, pt: 0.5, pb: 0.75 }} data-testid="transport-bar">
      {/* Slim scrub bar with note ticks and in/out */}
      <Box sx={{ position: 'relative', px: 0.75 }}>
        <Box sx={{ position: 'absolute', left: 6, right: 6, top: 4, height: 6, pointerEvents: 'none' }}>
          {typeof inPoint === 'number' && typeof outPoint === 'number' && outPoint > inPoint && (
            <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: `${percent(inPoint)}%`, width: `${percent(outPoint) - percent(inPoint)}%`, bgcolor: studioDark.rangeFill, borderRadius: 1 }} />
          )}
          {markers.map((marker, index) => (
            <Box
              key={`${marker.time}-${index}`}
              sx={{
                position: 'absolute',
                top: -3,
                height: 12,
                left: `${percent(marker.time)}%`,
                width: marker.endTime && marker.endTime > marker.time ? `max(2px, ${percent(marker.endTime) - percent(marker.time)}%)` : '2px',
                // Lifted so the dark category hues still read on the dark bar.
                bgcolor: markOnDark(marker.color),
                borderRadius: '1px',
              }}
            />
          ))}
        </Box>
        <Slider
          aria-label="Seek"
          size="small"
          min={0}
          max={Math.max(duration, 0.001)}
          step={1 / Math.max(1, fps)}
          value={Math.min(currentTime, duration || currentTime)}
          disabled={disabled || duration <= 0}
          onChange={(_, value) => player.seek(value as number)}
          valueLabelDisplay="auto"
          valueLabelFormat={(value) => formatTimecode(value, fps)}
          sx={{
            py: '6px !important',
            color: studioDark.accent,
            height: 4,
            '& .MuiSlider-rail': { bgcolor: studioDark.rail, opacity: 1 },
            '& .MuiSlider-track': { border: 0 },
            '& .MuiSlider-thumb': { width: 12, height: 12, bgcolor: studioDark.text, '&:hover, &.Mui-focusVisible': { boxShadow: `0 0 0 6px ${studioDark.accentHalo}` } },
            '& .MuiSlider-valueLabel': { fontVariantNumeric: 'tabular-nums', fontFamily: TIMECODE_FONT, fontSize: 11, bgcolor: studioDark.raised, color: studioDark.text },
          }}
        />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0, sm: 0.25 }, flexWrap: 'wrap' }}>
        <Tooltip title="Back 10s (Shift + ←)">
          <span>
            <IconButton size="small" aria-label="Back 10 seconds" onMouseDown={keepFocus} onClick={() => player.skip(-10)} disabled={disabled} sx={{ ...controlSx, display: wideOnly }}>
              <Replay10Icon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Back 5s (←)">
          <span>
            <IconButton size="small" aria-label="Back 5 seconds" onMouseDown={keepFocus} onClick={() => player.skip(-5)} disabled={disabled} sx={{ ...controlSx, display: compact ? 'none' : 'inline-flex' }}>
              <Replay5Icon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Previous frame (,)">
          <span>
            <IconButton size="small" aria-label="Previous frame" onMouseDown={keepFocus} onClick={() => player.stepFrames(-1)} disabled={disabled} sx={controlSx}>
              <ChevronLeftIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={playing ? 'Pause (Space / K)' : 'Play (Space / L)'}>
          <span>
            <IconButton
              aria-label={playing ? 'Pause' : 'Play'}
              onMouseDown={keepFocus}
              onClick={player.toggle}
              disabled={disabled}
              sx={{
                mx: 0.25,
                width: 38,
                height: 38,
                bgcolor: studioDark.buttonFill,
                color: studioDark.buttonText,
                '&:hover': { bgcolor: studioDark.buttonFillHover },
                '&.Mui-disabled': { bgcolor: studioDark.disabledFill, color: studioDark.buttonText },
                '&:focus-visible': { outline: `2px solid ${studioDark.accent}`, outlineOffset: 2 },
              }}
            >
              {playing || shuttle < 0 ? <PauseIcon /> : <PlayArrowIcon />}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Next frame (.)">
          <span>
            <IconButton size="small" aria-label="Next frame" onMouseDown={keepFocus} onClick={() => player.stepFrames(1)} disabled={disabled} sx={controlSx}>
              <ChevronRightIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Forward 5s (→)">
          <span>
            <IconButton size="small" aria-label="Forward 5 seconds" onMouseDown={keepFocus} onClick={() => player.skip(5)} disabled={disabled} sx={{ ...controlSx, display: compact ? 'none' : 'inline-flex' }}>
              <Forward5Icon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Forward 10s (Shift + →)">
          <span>
            <IconButton size="small" aria-label="Forward 10 seconds" onMouseDown={keepFocus} onClick={() => player.skip(10)} disabled={disabled} sx={{ ...controlSx, display: wideOnly }}>
              <Forward10Icon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, ml: 1, minWidth: 0 }}>
          <Typography
            component="span"
            data-testid="transport-timecode"
            sx={{ fontFamily: TIMECODE_FONT, fontVariantNumeric: 'tabular-nums', fontSize: 14, fontWeight: 600, letterSpacing: '0.02em', color: studioDark.text, whiteSpace: 'nowrap' }}
          >
            {formatTimecode(currentTime, fps)}
          </Typography>
          <Typography
            component="span"
            sx={{ fontFamily: TIMECODE_FONT, fontVariantNumeric: 'tabular-nums', fontSize: 12, color: studioDark.textSubtle, whiteSpace: 'nowrap', display: compact ? 'none' : { xs: 'none', sm: 'inline' } }}
          >
            / {formatTimecode(duration, fps)}
          </Typography>
          {shuttling && (
            <Typography
              component="span"
              aria-live="polite"
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, alignSelf: 'center', fontSize: 11, fontWeight: 700, color: studioDark.accent, ml: 0.5, fontVariantNumeric: 'tabular-nums', '& svg': { fontSize: 16 } }}
            >
              {shuttle < 0 ? <FastRewindIcon aria-hidden="true" /> : <FastForwardIcon aria-hidden="true" />}
              <Box component="span" sx={visuallyHidden}>{shuttle < 0 ? 'Shuttling back at ' : 'Shuttling forward at '}</Box>
              {Math.abs(shuttle)}×
            </Typography>
          )}
          {player.buffering && playing && (
            <Typography component="span" sx={{ fontSize: 11, color: studioDark.textSubtle, ml: 0.5 }}>
              Buffering…
            </Typography>
          )}
        </Box>

        <Box sx={{ flex: 1 }} />

        {loop && (
          <Tooltip title={loop.available ? (loop.enabled ? 'Looping In → Out (Shift + L)' : 'Loop In → Out (Shift + L)') : 'Mark In and Out (I / O) to loop a range'}>
            <span>
              <IconButton
                size="small"
                aria-label="Loop in/out range"
                aria-pressed={loop.enabled}
                onMouseDown={keepFocus}
                onClick={loop.onToggle}
                disabled={disabled || !loop.available}
                sx={{ ...controlSx, color: loop.enabled ? studioDark.accent : controlSx.color }}
              >
                <RepeatIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}

        <Tooltip title="Playback speed">
          <span>
            <IconButton
              size="small"
              aria-label={`Playback speed ${player.rate}x`}
              aria-haspopup="menu"
              onMouseDown={keepFocus}
              onClick={(event) => setSpeedAnchor(event.currentTarget)}
              disabled={disabled}
              sx={{ ...controlSx, fontSize: 12, fontWeight: 700, borderRadius: 1.5, px: 1, minWidth: 44, fontVariantNumeric: 'tabular-nums' }}
            >
              {player.rate}×
            </IconButton>
          </span>
        </Tooltip>
        <Menu anchorEl={speedAnchor} open={Boolean(speedAnchor)} onClose={() => setSpeedAnchor(null)}>
          {PLAYBACK_SPEEDS.map((speed) => (
            <MenuItem
              key={speed}
              selected={speed === player.rate}
              onClick={() => {
                player.setRate(speed);
                setSpeedAnchor(null);
              }}
              sx={{ fontVariantNumeric: 'tabular-nums', minWidth: 96 }}
            >
              {speed}×
            </MenuItem>
          ))}
        </Menu>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, width: compact ? 'auto' : { xs: 'auto', sm: 132 } }}>
          <Tooltip title={player.muted ? 'Unmute (M)' : 'Mute (M)'}>
            <IconButton size="small" aria-label={player.muted ? 'Unmute' : 'Mute'} onMouseDown={keepFocus} onClick={player.toggleMute} sx={controlSx}>
              {player.muted || player.volume === 0 ? (
                <VolumeOffIcon fontSize="small" />
              ) : player.volume < 0.5 ? (
                <VolumeDownIcon fontSize="small" />
              ) : (
                <VolumeUpIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
          <Slider
            aria-label="Volume"
            size="small"
            min={0}
            max={1}
            step={0.05}
            value={player.muted ? 0 : player.volume}
            onChange={(_, value) => player.setVolume(value as number)}
            sx={{
              display: compact ? 'none' : { xs: 'none', sm: 'block' },
              color: studioDark.text,
              '& .MuiSlider-rail': { bgcolor: studioDark.rail, opacity: 1 },
              '& .MuiSlider-thumb': { width: 10, height: 10 },
            }}
          />
        </Box>

        {extra}

        {onToggleFullscreen && (
          <Tooltip title={isFullscreen ? 'Exit full screen (F)' : 'Full screen (F)'}>
            <IconButton size="small" aria-label={isFullscreen ? 'Exit full screen' : 'Full screen'} onMouseDown={keepFocus} onClick={onToggleFullscreen} sx={controlSx}>
              {isFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

export default TransportBar;
